// Inline "Schedule New Holiday" form panel — mirrors the gradient form
// from the legacy `manage_holidays.php` page.

import { useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarPlus } from 'lucide-react';
import { Button } from '../../../../components/ui/Button/Button';
import { Select } from '../../../../components/form/Select';
import { DatePicker } from '../../../../components/form/DatePicker';
import { Textarea } from '../../../../components/form/Textarea';
import { listStaff } from '../../../../api/users';
import { listDrivers } from '../../../../api/drivers';
import { queryKeys } from '../../../../api/queryKeys';
import { useCreateHoliday } from '../hooks/useHolidays';
import type { HolidayEntityType, HolidayPeriod } from '../../../../api/holidays';
import styles from './ScheduleHolidayForm.module.css';

function todayIsoLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function ScheduleHolidayForm(): JSX.Element {
  const [entityType, setEntityType] = useState<HolidayEntityType | ''>('');
  const [entityUuid, setEntityUuid] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [period, setPeriod] = useState<HolidayPeriod>('FULL');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(todayIsoLocal, []);
  const create = useCreateHoliday();

  // Pre-fetch both lists so switching the type doesn't flash empty.
  const staffQuery = useQuery({
    queryKey: queryKeys.users.list({ scope: 'holidays-form', status: 'active' }),
    queryFn: () => listStaff({ page: 1, limit: 100, status: 'active' }),
    enabled: entityType === 'staff',
    staleTime: 60_000,
  });
  const driversQuery = useQuery({
    queryKey: queryKeys.driversAdmin.list({ scope: 'holidays-form', status: 'active' }),
    queryFn: () => listDrivers({ page: 1, limit: 100, status: 'active' }),
    enabled: entityType === 'driver',
    staleTime: 60_000,
  });

  const personOptions = useMemo(() => {
    if (entityType === 'staff') {
      return (staffQuery.data?.staff ?? []).map((s) => ({
        value: s.uuid,
        label: s.email ? `${s.name} (${s.email})` : s.name,
      }));
    }
    if (entityType === 'driver') {
      return (driversQuery.data?.drivers ?? []).map((d) => ({
        value: d.uuid,
        label: d.email ? `${d.name} (${d.email})` : d.name,
      }));
    }
    return [];
  }, [entityType, staffQuery.data, driversQuery.data]);

  function reset() {
    setEntityType('');
    setEntityUuid('');
    setStart('');
    setEnd('');
    setPeriod('FULL');
    setNotes('');
    setError(null);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!entityType) {
      setError('Please select an employee type.');
      return;
    }
    if (!entityUuid) {
      setError('Please select an employee.');
      return;
    }
    if (!start || !end) {
      setError('Please select both start and end dates.');
      return;
    }
    if (end < start) {
      setError('Holiday end date must be after or equal to start date.');
      return;
    }
    create.mutate(
      {
        entity_type: entityType,
        entity_uuid: entityUuid,
        holiday_start: start,
        holiday_end: end,
        period,
        notes: notes.trim() === '' ? null : notes.trim(),
      },
      { onSuccess: () => reset() },
    );
  }

  const endMin = start !== '' ? start : today;

  return (
    <div className={styles.panel}>
      <h3 className={styles.heading}>
        <CalendarPlus size={18} aria-hidden /> Schedule New Holiday
      </h3>
      <form onSubmit={handleSubmit} aria-label="Schedule new holiday" noValidate>
        <div className={styles.row}>
          <Select
            label="Employee Type"
            required
            value={entityType}
            onChange={(e) => {
              const v = e.target.value as HolidayEntityType | '';
              setEntityType(v);
              setEntityUuid('');
            }}
            placeholder="Select Type"
            options={[
              { value: 'staff', label: 'Staff' },
              { value: 'driver', label: 'Driver' },
            ]}
          />
          <Select
            label="Select Person"
            required
            value={entityUuid}
            onChange={(e) => setEntityUuid(e.target.value)}
            disabled={!entityType}
            placeholder={
              entityType === '' ? 'Select Employee Type First' : 'Select Person'
            }
            options={personOptions}
          />
          <DatePicker
            label="Holiday Start Date"
            required
            value={start}
            minDate={today}
            onChange={(e) => setStart(e.target.value)}
          />
          <DatePicker
            label="Holiday End Date"
            required
            value={end}
            minDate={endMin}
            onChange={(e) => setEnd(e.target.value)}
          />
          <Select
            label="Period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as HolidayPeriod)}
            options={[
              { value: 'FULL', label: 'Full Day' },
              { value: 'AM', label: 'AM Only' },
              { value: 'PM', label: 'PM Only' },
            ]}
          />
        </div>
        <Textarea
          label="Notes (Optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional notes about the holiday..."
          rows={3}
        />
        {error ? (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        ) : null}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" icon={CalendarPlus} loading={create.isPending}>
            Schedule Holiday
          </Button>
        </div>
      </form>
    </div>
  );
}
